import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import {
  Tag,
  TagEditorEntity,
  TagEditorStateModel,
  TagRepository,
  TagRepositoryRegisterPayload,
  TagRepositoryUpdatePayload,
} from '../../models/tag.model';

// identifier
export const TAG_EDITOR_SLICE_ID = 'tagEditor';

// default state
const initialState: TagEditorStateModel = {
  visible: false,
  id: -1,
  objectType: null,
  tags: [],
  tagRepository: {},
};

//slice
const tagEditorSlice = createSlice({
  name: TAG_EDITOR_SLICE_ID,
  initialState,
  reducers: {
    setTagEditorVisible: (state: TagEditorStateModel, action: PayloadAction<boolean>): void => {
      state.visible = action.payload;
    },
    setTagEditorObjectId: (state: TagEditorStateModel, action: PayloadAction<number>): void => {
      state.id = action.payload;
    },
    setTagEditorObjectType: (
      state: TagEditorStateModel,
      action: PayloadAction<TagEditorEntity>
    ): void => {
      state.objectType = action.payload;
    },
    setTagEditorTags: (state: TagEditorStateModel, action: PayloadAction<Tag[]>): void => {
      state.tags = action.payload;
    },
    resetTagEditor: (state: TagEditorStateModel): void => {
      state.visible = false;
      state.id = -1;
      state.objectType = null;
      state.tags = [];
    },
    setTagRepository: (state: TagEditorStateModel, action: PayloadAction<TagRepository>) => {
      state.tagRepository = action.payload;
    },
    addTagRepository: (
      state: TagEditorStateModel,
      action: PayloadAction<TagRepositoryRegisterPayload>
    ) => {
      if (!state.tagRepository[action.payload.repository]) {
        state.tagRepository[action.payload.repository] = {};
      }
      state.tagRepository[action.payload.repository] = action.payload.records;
    },
    addTagsToObjectInRepository: (
      state: TagEditorStateModel,
      action: PayloadAction<TagRepositoryUpdatePayload>
    ) => {
      action.payload.ids.forEach((id: number) => {
        if (!state.tagRepository[action.payload.repository][id]) {
          state.tagRepository[action.payload.repository][id] = [];
        }
        const itemTags: { [key: string]: unknown } = {};
        Object.keys(action.payload.tags).forEach((tag: string) => {
          if (!Object.values(itemTags).includes(tag)) {
            itemTags[tag] = action.payload.tags[tag];
          }
        });

        state.tagRepository[action.payload.repository][id] = {
          ...state.tagRepository[action.payload.repository][id],
          ...itemTags,
        };
      });
    },
    updateTagsInRepository: (
      state: TagEditorStateModel,
      action: PayloadAction<TagRepositoryUpdatePayload>
    ) => {
      action.payload.ids.forEach((id: number) => {
        if (!state.tagRepository[action.payload.repository][id]) {
          state.tagRepository[action.payload.repository][id] = [];
        }
        const itemTags: { [key: string]: unknown } = {};
        Object.keys(action.payload.tags).forEach((tag: string) => {
          if (!Object.values(itemTags).includes(tag)) {
            itemTags[tag] = action.payload.tags[tag];
          }
        });

        state.tagRepository[action.payload.repository][id] = itemTags;
      });
    },
  },
});

//selectors
export const selectTagEditorVisible = (state: RootState) => state.tagEditor.visible;

export const selectTagEditorId = (state: RootState) => state.tagEditor.id;

export const selectTagEditorObjectType = (state: RootState) => state.tagEditor.objectType;

export const selectTagEditorTags = (state: RootState) => state.tagEditor.tags;

export const selectTagRepository = (state: RootState) => state.tagEditor.tagRepository;

//actions
export const {
  setTagEditorVisible,
  setTagEditorObjectId,
  setTagEditorObjectType,
  setTagEditorTags,
  resetTagEditor,
  setTagRepository,
  updateTagsInRepository,
  addTagRepository,
  addTagsToObjectInRepository,
} = tagEditorSlice.actions;

//reducer
export default tagEditorSlice;
